import { ContentKindType } from '@/constants/contents';

import { CodebaseReportStats } from './codebase';
import { SourceRole, SourceVisibility } from './sources';

export interface VersionCreator {
  id: string;
  full_name: string;
  email: string;
}

// The underlying Node entity (deduplicated content identity)
// Note: contents is only present in some response contexts (e.g., NodeReadWithTerseSentence)
export interface VersionNodeNode {
  created_at: string;
  id: string;
  kind: string;
  primary_asset_id: string;
  updated_at: string;
}

export interface RootVersionNode {
  id: string;
  version_id: string;
  relative_path: string;
  created_at: string;
  updated_at: string;
  depth: number;
  total_files?: number;
  misc_metadata?: CodebaseReportStats;
  node: VersionNodeNode;
  node_id: string;
}

/**
 * Core version fields (shared by all version types)
 */
export interface VersionCore {
  id: string;
  primary_asset_id: string;
  vcs_hash?: string | null;
  created_at: string;
  updated_at: string;
  status: VersionStatusType;
  browsable?: boolean;
  vcs_metadata?: VersionControl;
}

/**
 * Version with primary_asset and creator (no root_version_node)
 * Used when version is nested inside a version_node
 */
export interface VersionWithDetails extends VersionCore {
  primary_asset: PrimaryAssetCore;
  creator?: VersionCreator;
}

/**
 * Version with root_version_node (for endpoints that return versions directly)
 * Used by: /node/versions: VersionResponse
 */
export interface VersionRecord extends VersionWithDetails {
  root_version_node?: RootVersionNode;
}

/**
 * Primarily used by `most_recent_version` and `most_recent_completed_version`.
 */
export interface VersionDetail extends VersionCore {
  creator?: VersionCreator;
  root_version_node?: RootVersionNode;
  vcs_metadata?: VersionControl;
}

/**
 * Version node with nested version (for /node/contents)
 * No Omit needed - uses VersionWithDetails directly
 */
export interface ContentVersionNode extends RootVersionNode {
  version: VersionWithDetails;
}

export interface PrimaryAssetCore {
  id: string;
  organization_id: string;
  kind: PrimaryAssetType;
  display_name: string;
  provider?: ProviderType;
  created_at: string;
  updated_at: string;
}

export interface PrimaryAssetLite extends PrimaryAssetCore {
  effective_role: SourceRole;
  status: VersionStatusType;
}

export interface PrimaryAssetRecord extends PrimaryAssetLite {
  content?: string; // TODO: node_dedupe: Is this returned for pages.
  browsable?: boolean; // TODO: node_dedupe: is this actually optional?
  tags?: any[]; // FUTURE: Add types.
  most_recent_version?: VersionDetail;
  most_recent_completed_version?: VersionDetail;
  codebase_settings_auto_commit_docs?: boolean;
  repository_id?: string;
  visibility: SourceVisibility;
  is_user_favorited?: boolean;
}

export type ErrorType = 'connection_failed' | 'generation_error' | 'insufficient_balance';

export interface BranchFilterConfig {
  mode: 'allow' | 'block';
  allow_patterns: string[];
  block_patterns: string[];
  max_patterns: number;
}

export interface CodebaseCardRecord extends PrimaryAssetLite {
  source_type: SourceType;
  version_control: VersionControl;
  codebase_settings_auto_commit_docs: boolean;
  error_type?: ErrorType | null;
  error_message?: string | null;
  vcs_auto_update_policy: string;
  browsable: boolean;
  tags: any[];
  most_recent_metadata: Pick<
    CodebaseReportStats,
    | 'analyzable_files_by_type'
    | 'analyzable_sloc'
    | 'analyzable_sloc_by_type'
    | 'driver_ignored_files'
    | 'top_language'
    | 'total_files'
    | 'total_sloc'
  > & {
    id: string;
    root_node_id: string;
    root_version_node_id: string;
    status: VersionStatusType;
  };
  most_recent_version_content: {
    root_node_id: string | null;
    kind: string[] | null;
    domain: string[] | null;
    audience: string[] | null;
    content: string | null;
  } | null;
  visibility: SourceVisibility;
  is_user_favorited: boolean;
  branch_filter_config: BranchFilterConfig;
}

export interface VersionControl {
  provider: ProviderType;
  repository: {
    name: string;
    namespace: string;
    full_name: string;
    url: string;
  };
  branch: {
    name: string;
    protected: boolean;
  };
  commit: {
    sha: string;
    short_sha: string;
    message: string;
    url: string;
    author: {
      date: string;
      name: string;
      email: string;
    };
  };
}

export const PrimaryAsset = {
  FILE: 'FILE',
  CODEBASE: 'CODEBASE',
  CODEBASE_DIRECTORY: 'CODEBASE_DIRECTORY',
  PAGE: 'PAGE',
} as const;

export type PrimaryAssetType = (typeof PrimaryAsset)[keyof typeof PrimaryAsset];

export const ColumnFiltersAssetType = ['FILE', 'CODEBASE', 'PAGE'] as const;

export type ColumnFiltersAssetType = (typeof ColumnFiltersAssetType)[number];

export interface PrimaryAssetResponse {
  results: PrimaryAssetRecord[];
  total_count: number;
}

export interface CodebaseCardResponse {
  results: CodebaseCardRecord[];
  total_count: number;
}

export interface VersionResponse {
  results: VersionRecord[];
  total_count: number;
}

export interface CodebaseBranch {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
  most_recent_connected_version_id: string | null;
  has_browsable_versions: boolean;
  last_commit_date: string | null;
  browsable_version_status: VersionStatusType | null;
}

export interface CodebaseBranchesResponse {
  branches: CodebaseBranch[];
  total_count: number;
}

export interface ListCodebaseBranchesParams {
  codebaseId: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_direction?: 'ASC' | 'DESC';
  status?: string[];
  ids?: string[];
  has_browsable_versions?: boolean;
}

export const VersionStatus = {
  GENERATION_COMPLETE: 'GENERATION_COMPLETE',
  GENERATING: 'GENERATING',
  GENERATION_ERROR: 'GENERATION_ERROR',
  CONNECTED: 'CONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTION_FAILED: 'CONNECTION_FAILED',
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
} as const;

export type VersionStatusType = (typeof VersionStatus)[keyof typeof VersionStatus];

export const SourceType = {
  GITHUB_CODEBASE: 'Github Codebase',
  GITLAB: 'Gitlab Codebase',
  BITBUCKET: 'Bitbucket Codebase',
  BITBUCKET_DATA_CENTER: 'Bitbucket Data Center Codebase',
  AZURE_DEVOPS: 'Azure DevOps Codebase',
  PDF_UPLOAD: 'PDF Upload',
  ZIP_UPLOAD: 'Zip Upload',
} as const;

export type SourceType = (typeof SourceType)[keyof typeof SourceType];

export const Provider = {
  GITHUB: 'github',
  GITLAB: 'gitlab',
  BITBUCKET: 'bitbucket',
  BITBUCKET_DATA_CENTER: 'bitbucket-dc',
  AZURE_DEVOPS: 'azure-devops',
  USER: 'user',
} as const;

export type ProviderType = (typeof Provider)[keyof typeof Provider];

// Display labels live in `@/components/StatusIndicators/statusLabels`. The
// previous flat VersionStatusDisplayName map collapsed every render
// context to one string; the labels module supports short/long/verb_ing
// variants for the per-row badge, banner copy, and toolbar filter.

export const BrowsableVersionStatuses: VersionStatusType[] = [
  VersionStatus.GENERATION_COMPLETE,
  VersionStatus.GENERATING,
  VersionStatus.GENERATION_ERROR,
] as const;

export const SelectableVersionStatuses: VersionStatusType[] = [
  VersionStatus.GENERATION_COMPLETE,
  VersionStatus.GENERATING,
  VersionStatus.CONNECTED,
] as const;

export const ErrorVersionStatuses: VersionStatusType[] = [
  VersionStatus.GENERATION_ERROR,
  VersionStatus.CONNECTION_FAILED,
  VersionStatus.INSUFFICIENT_BALANCE,
] as const;

export const isErrorStatus = (status?: VersionStatusType): boolean =>
  status !== undefined && ErrorVersionStatuses.includes(status);

export interface ContentRecord {
  id: string;
  content_kind: ContentKindType;
  content_name: string | null;
  node_id: string | null;
  content: string | null;
  misc_metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  version_node: ContentVersionNode;
}

export interface ContentResponse {
  results: ContentRecord[];
  total_count: number;
}

export interface FetchPrimaryAssetParams {
  id?: string;
  id__in?: string;
  display_name__ilike?: string;
  display_name__in?: string;
  document_source_ids?: string[];
  limit: number;
  offset: number;
  kind?: PrimaryAssetType[];
  sort_by?: string;
  sort_direction?: 'ASC' | 'DESC';
  tag_ids?: string[];
  'branches.versions.status'?: VersionStatusType | VersionStatusType[];
  top_language?: string[];
  // 'versions.most_recent_metadata.top_language'?: string[];
  created_at__gte?: string;
  created_at__lte?: string;
  updated_at__gte?: string;
  updated_at__lte?: string;
  content_scope?: 'org' | 'codebase';
  favorites_only?: boolean;
}

export interface FetchVersionParams {
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_direction?: 'ASC' | 'DESC';
  id?: string;
  primary_asset_id?: string;
  branch_id?: string;
  status?: VersionStatusType | VersionStatusType[];
}

export interface UpdateVersionParams {
  id: string;
  vcs_hash?: string | null;
  status?: VersionStatusType;
}

export interface FetchContentParams {
  version_node_id: string;
  content_kind?: string | string[];
  include_content?: boolean;
  limit?: number;
  offset?: number;
  id?: string;
}

export interface SourceAutodocItem {
  id: string;
  display_name: string;
  type: 'autodoc' | 'page';
  created_at: string;
  updated_at: string;
  creator?: VersionCreator;
  tags?: any[];
  rootVersionNodeId?: string;
}

export interface SourceAutodocsResponse {
  docs: SourceAutodocItem[];
  total: number;
}

export interface UsageEstimateResponse {
  deduplicated_sloc: number;
  deduplicated_sloc_by_type: Record<string, number>;
  deduplicated_files_by_type: Record<string, number>;
}
